import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToProjectEvents } from 'src/sockets/ContentGenSocket';
import { useProjectFlowStore } from '../../stores';

interface UseProjectsSubscriptionOptions {
  enabled?: boolean;
}

/**
 * Subscribes to project events and invalidates the React Query cache
 * used by the Projects list (ProjectTable).
 */
export const useProjectsSubscription = ({ enabled = true }: UseProjectsSubscriptionOptions = {}) => {
  const queryClient = useQueryClient();

  const handleMessage = useCallback(
    (data: { projectId: string; name?: string }) => {
      // Invalidate list and single-project queries
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
      queryClient.invalidateQueries({ queryKey: ['project', data?.projectId] });

      // Sync store if event matches current project
      const { projectId: currentProjectId, setProjectDetails } = useProjectFlowStore.getState();
      if (currentProjectId && data?.projectId === currentProjectId && typeof data?.name === 'string') {
        setProjectDetails({ projectName: data.name });
      }
    },
    [queryClient]
  );

  const handleError = useCallback((error: any) => {
    console.error('❌ Projects subscription error:', error);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let subscriptionObj: { unsubscribe: () => void } | null = null;

    const setup = async () => {
      subscriptionObj = await subscribeToProjectEvents({ onMessage: handleMessage, onError: handleError });
    };

    setup();

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      if (subscriptionObj) {
        subscriptionObj.unsubscribe();
      }
    };
  }, [enabled, handleMessage, handleError]);
};
